import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { formatCurrency, formatStockCantidad } from '../../../utils/formatters';
import {
  parseCantidadPedido,
  validarCantidadesPedido,
} from '../../../utils/cantidadesPedido';

export interface CantidadesPedidoDialogValores {
  cantidad: number;
  cantidadesPES: number;
  cantidadCanc: number;
}

interface CantidadesPedidoDialogProps {
  open: boolean;
  descripcion: string;
  um?: string;
  stockDisponible?: number | null;
  cantidad: number;
  cantidadesPES: number;
  cantidadCanc: number;
  /** Importe del renglón para una cantidad dada. No descuenta espera ni cancelada. */
  importeDeCantidad: (cantidad: number) => number;
  onCancel: () => void;
  onAceptar: (valores: CantidadesPedidoDialogValores) => void;
}

const campoCantidad = (value: number): string =>
  Number.isFinite(value) ? String(value) : '';

const escribirEntero = (raw: string): string | null => {
  if (raw === '') return '';
  return /^\d+$/.test(raw) ? raw : null;
};

const CantidadesPedidoDialog: React.FC<CantidadesPedidoDialogProps> = ({
  open,
  descripcion,
  um,
  stockDisponible,
  cantidad,
  cantidadesPES,
  cantidadCanc,
  importeDeCantidad,
  onCancel,
  onAceptar,
}) => {
  const [cantidadRaw, setCantidadRaw] = useState('');
  const [esperaRaw, setEsperaRaw] = useState('');
  const [canceladaRaw, setCanceladaRaw] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCantidadRaw(campoCantidad(cantidad));
    setEsperaRaw(campoCantidad(cantidadesPES));
    setCanceladaRaw(campoCantidad(cantidadCanc));
    setError(null);
  }, [open, cantidad, cantidadesPES, cantidadCanc]);

  const cantidadNum = parseCantidadPedido(cantidadRaw);
  const esperaNum = parseCantidadPedido(esperaRaw);
  const canceladaNum = parseCantidadPedido(canceladaRaw);
  const total = importeDeCantidad(cantidadNum);
  const umLabel = String(um ?? '').trim();
  const stockLabel =
    stockDisponible == null || !Number.isFinite(Number(stockDisponible))
      ? '—'
      : `${formatStockCantidad(Number(stockDisponible))}${umLabel ? ` ${umLabel}` : ''}`;

  const aceptar = () => {
    const mensaje = validarCantidadesPedido({
      cantidad: cantidadNum,
      enEspera: esperaNum,
      cancelada: canceladaNum,
    });
    if (mensaje) {
      setError(mensaje);
      return;
    }
    onAceptar({
      cantidad: cantidadNum,
      cantidadesPES: esperaNum,
      cantidadCanc: canceladaNum,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Cantidades del pedido
        </Typography>
      </DialogTitle>
      <DialogContent>
        {descripcion ? (
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {descripcion}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Stock disponible: {stockLabel}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Cantidad"
            size="small"
            value={cantidadRaw}
            onChange={(e) => {
              const next = escribirEntero(e.target.value);
              if (next == null) return;
              setCantidadRaw(next);
              setError(null);
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'right' } }}
            fullWidth
          />
          <TextField
            label="A pedido en espera"
            size="small"
            value={esperaRaw}
            onChange={(e) => {
              const next = escribirEntero(e.target.value);
              if (next == null) return;
              setEsperaRaw(next);
              setError(null);
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'right' } }}
            autoFocus
            fullWidth
          />
          <TextField
            label="Cancelada"
            size="small"
            value={canceladaRaw}
            onChange={(e) => {
              const next = escribirEntero(e.target.value);
              if (next == null) return;
              setCanceladaRaw(next);
              setError(null);
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'right' } }}
            fullWidth
          />
          <TextField
            label="Total del renglón"
            size="small"
            value={formatCurrency(total)}
            InputProps={{ readOnly: true }}
            fullWidth
            helperText="Se calcula sobre la cantidad del renglón."
          />
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button variant="contained" onClick={aceptar}>
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CantidadesPedidoDialog;
